import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as userApi from '../../lib/api/user';

const MAX_STEPS = 8;

const ApprovalStepEditor = ({
  steps,
  onChange,
  currentUserUuid,
  disabled = false,
}) => {
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      try {
        const res = await userApi.getUsers();
        if (mounted) {
          setUserOptions(res.data.list || []);
        }
      } catch (e) {
        if (mounted) {
          setUserOptions([]);
        }
      }
    };

    fetchUsers();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredOptions = useMemo(
    () => userOptions.filter((user) => user.usrUuid !== currentUserUuid),
    [userOptions, currentUserUuid],
  );

  const handleStepChange = (index, value) => {
    const nextSteps = [...steps];
    nextSteps[index] = value || '';
    onChange(nextSteps);
  };

  const handleAdd = () => {
    onChange([...steps, '']);
  };

  const handleRemove = (index) => {
    const nextSteps = steps.filter((_, currentIndex) => currentIndex !== index);
    onChange(nextSteps.length > 0 ? nextSteps : ['']);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">결재 단계</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={disabled || steps.length >= MAX_STEPS}
        >
          단계 추가
        </Button>
      </Stack>

      {steps.map((stepUsrUuid, index) => {
        const selectedUser = filteredOptions.find((user) => user.usrUuid === stepUsrUuid) || null;
        const duplicated = stepUsrUuid && steps.some(
          (value, compareIndex) => compareIndex !== index && value === stepUsrUuid,
        );

        return (
          <Stack key={`${index}-${stepUsrUuid || 'empty'}`} direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete
              options={filteredOptions}
              getOptionLabel={(option) => `${option.usrNm}${option.dptNm ? ` (${option.dptNm})` : ''}`}
              value={selectedUser}
              onChange={(_event, newValue) => handleStepChange(index, newValue?.usrUuid || '')}
              fullWidth
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${index + 1}차 결재자${index === 0 ? ' *' : ''}`}
                  size="small"
                  error={Boolean(duplicated)}
                  helperText={duplicated ? '같은 결재자를 중복 지정할 수 없습니다.' : ' '}
                />
              )}
            />
            <IconButton
              color="error"
              onClick={() => handleRemove(index)}
              disabled={disabled || steps.length === 1}
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ApprovalStepEditor;
